import { Box, Button, Typography, CircularProgress } from '@mui/material'
import { useOktaAuth } from '@okta/okta-react'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CustomUserClaim } from '@okta/okta-auth-js'

import { API } from '../services/endpoints'
import { Application, ADMIN_GROUPS } from '../Types/Application'
import { ReactComponent as Bet365LogoBlack } from '../assets/Bet365LogoBlack.svg'
import { ReactComponent as Bet365Logo } from '../assets/Bet365LogoWhite.svg'
import { ReactComponent as EmployeePortalLogo } from '../assets/EmployeePortalLogo.svg'
import { ReactComponent as EmployeePortalLogoGreen } from '../assets/EmployeePortalLogoGreen.svg'
import { ReactComponent as LockIcon } from '../assets/IconFeatherLock.svg'

const getEdgeDeepLink = (url: string): string => {
  const stripped = url.replace(/^https?:\/\//, '')
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    return `microsoft-edge-https://${stripped}`
  }
  if (/Android/i.test(navigator.userAgent)) {
    return `microsoft-edge://${stripped}`
  }
  return `microsoft-edge:${url}`
}

// Detects whether the current device is an iOS device. Modern iPads report
// a `MacIntel` platform in their userAgent (desktop-class UA) while still
// exposing a touch screen, so we detect those via `maxTouchPoints` rather
// than relying on the userAgent string alone.
const isIOSDevice = (): boolean => {
  const userAgent = navigator.userAgent
  const platform =
    navigator.platform ||
    // Some browsers no longer expose `navigator.platform`; fall back to the
    // userAgentData platform where available, then to the userAgent string.
    (navigator as Navigator & { userAgentData?: { platform?: string } })
      .userAgentData?.platform ||
    userAgent

  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return true
  }
  // iPadOS 13+ identifies as a Mac (platform === 'MacIntel') but, unlike a
  // real Mac, is touch-enabled.
  if (/Mac/i.test(platform) && navigator.maxTouchPoints > 0) {
    return true
  }
  return false
}

// Confirmed correct iOS App Store id for Microsoft Edge (see commit f1e6e16,
// which fixed this from the previously-incorrect id1288713123).
const EDGE_APP_STORE_URL =
  'https://apps.apple.com/app/microsoft-edge-ai-browser/id1288723196'

// If the page is visible again this soon after attempting the Edge deep
// link, we treat it as "Edge is not installed" rather than "user switched
// apps and came back later".
const EDGE_FALLBACK_VISIBILITY_THRESHOLD_MS = 5000

export const Applications: React.FC = () => {
  const { oktaAuth, authState } = useOktaAuth()
  const [userRoles, setUserRoles] = useState<CustomUserClaim[]>([])
  const navigate = useNavigate()

  const [loading, setLoading] = React.useState(true)
  const [error, setError] = useState<string | null>(null)
  const [applications, setApplications] = useState<Application[]>([])
  const [showEdgeInstallPrompt, setShowEdgeInstallPrompt] = useState(false)

  // Tracks the in-flight "did Edge open?" watcher so a rapid double-tap on
  // the Proactis tile clears the previous attempt before starting a new one.
  const edgeLaunchWatcherRef = useRef<{
    deepLinkStartedAt: number
    timeoutId: number
    cleanup: () => void
  } | null>(null)

  const clearEdgeLaunchWatcher = () => {
    edgeLaunchWatcherRef.current?.cleanup()
    edgeLaunchWatcherRef.current = null
  }

  // Clean up any pending watcher (listeners + timer) on unmount.
  useEffect(() => {
    return clearEdgeLaunchWatcher
  }, [])

  useEffect(() => {
    if (authState && authState.isAuthenticated) {
      oktaAuth.getUser().then((info) => {
        console.log('portal_roles: ', info['portal_roles'])
        if (info['portal_roles']) {
          setUserRoles(info['portal_roles'] as CustomUserClaim[])
        }
      })
    }
  }, [authState])

  const isAdmin = userRoles.some((roleClaim) =>
    ADMIN_GROUPS.some((group) => roleClaim.toString().includes(group)),
  )

  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true)
      try {
        const response = await API.GET('/Employee/applications')
        setApplications(response.applications || [])
      } catch (error) {
        setError('Failed to load applications')
        console.error('Error:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchApplications()
  }, [])

  // Starts (or restarts) the "did Edge actually open?" watcher for the
  // Proactis deep link. The install banner should only appear if the page
  // NEVER went hidden after the deep link was triggered — i.e. Edge never
  // launched (the `microsoft-edge-https://` scheme failed to resolve and
  // Safari stayed in the foreground). If the page goes hidden at all (Edge
  // took over) we permanently suppress the banner for this attempt, even
  // when Safari briefly flashes back to visible during the scheme handoff.
  //
  // Flow:
  // - On tap: clear previous state, attach listeners, start a 5s timer.
  // - On `hidden`/`pagehide`: set `didHide = true` and never show the
  //   banner for this attempt. Once `didHide` is true it stays true.
  // - On `visible`/`pageshow`/`focus`: if `didHide` is true, Edge opened
  //   successfully — do nothing (just clean up). If `didHide` is false and
  //   the timer hasn't expired, do nothing yet — keep waiting for the
  //   timer to decide.
  // - On timer expiry: if `didHide` is false (page was never hidden = Edge
  //   never launched), show the install banner. If `didHide` is true,
  //   don't show it. Clean up listeners + timer in all cases.
  const startEdgeLaunchWatcher = () => {
    // Guard against double-firing if the user taps the tile multiple times
    // quickly — tear down any previous watcher first.
    clearEdgeLaunchWatcher()

    const deepLinkStartedAt = Date.now()

    // Tracks whether the page actually went hidden after the deep link was
    // triggered. If Edge is installed it takes over and the page goes
    // hidden; if it isn't, the page never truly leaves the foreground.
    let didHide = false

    const handleHidden = () => {
      // The page went hidden — Edge (or another app) took over. Permanently
      // suppress the install banner for this attempt; a subsequent return
      // to visible must NOT re-trigger it.
      didHide = true
    }

    const handleBackToVisible = () => {
      // The page is visible again (Safari flashed back during the scheme
      // handoff or the user returned). If Edge already opened (`didHide`
      // is true) this is a successful launch — do nothing. If Edge hasn't
      // opened yet (`didHide` is false) keep waiting for the timer to
      // decide; never show the banner from this event.
      if (didHide) {
        clearWatcher()
      }
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleHidden()
      } else {
        handleBackToVisible()
      }
    }

    const clearWatcher = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', handleHidden)
      window.removeEventListener('pageshow', handleBackToVisible)
      window.removeEventListener('focus', handleBackToVisible)
      window.clearTimeout(timeoutId)
      edgeLaunchWatcherRef.current = null
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handleHidden)
    window.addEventListener('pageshow', handleBackToVisible)
    window.addEventListener('focus', handleBackToVisible)

    // The timer is the sole trigger for the install banner. When it fires,
    // the banner is shown only if the page never went hidden (Edge never
    // launched). If `didHide` is true, Edge opened successfully and we
    // silently clean up without showing anything.
    const timeoutId = window.setTimeout(() => {
      if (!didHide) {
        setShowEdgeInstallPrompt(true)
      }
      clearWatcher()
    }, EDGE_FALLBACK_VISIBILITY_THRESHOLD_MS)

    edgeLaunchWatcherRef.current = {
      deepLinkStartedAt,
      timeoutId,
      cleanup: clearWatcher,
    }
  }

  const handleLaunchApp = (app: Application) => {
    const isEdge = navigator.userAgent.includes('Edg')
    if (isEdge) {
      // Already in Edge — just open the URL normally (all platforms).
      window.open(app.link, '_blank')
    } else if (app.title === 'Proactis') {
      // Not in Edge and this is the Proactis tile — trigger the Edge deep
      // link for ALL platforms (desktop + iOS). The install-banner fallback
      // detection is iOS-only; desktop just gets the deep link with no
      // banner and no visibility watcher.
      if (isIOSDevice()) {
        setShowEdgeInstallPrompt(false)
        startEdgeLaunchWatcher()
      }
      window.location.href = getEdgeDeepLink(app.link)
    } else {
      window.open(app.link, '_blank')
    }
  }

  const handleLogout = async () => {
    await oktaAuth.signOut()
  }

  const sortedApps = [...applications].sort((a, b) =>
    a.title.localeCompare(b.title),
  )

  return (
    <>
      <Box className='applications'>
        <div className='applications__employeeLogoContainer'>
          <EmployeePortalLogoGreen className='applications__employeeLogo' />
        </div>
        <div className='applications__LeftContainer'>
          <div className='applications__aside-gradient' />
          <div className='applications__aside'>
            <div className='applications__aside-content'>
              <div className='applications__aside-logoContainer'>
                <EmployeePortalLogo className='applications__aside-exployeePortalLogo' />
                <Typography
                  variant='h4'
                  component='h1'
                  className='applications__aside-title'
                >
                  MY{' '}
                  <Box
                    component='span'
                    className='applications__aside-title--highlight'
                  >
                    APPS
                  </Box>
                </Typography>
              </div>
              <Bet365Logo className='applications__aside-bet365logo' />
            </div>
          </div>
        </div>
        <Box className='applications__content'>
          <div className='applications__container'>
            {loading ? (
              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                minHeight='200px'
              >
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                minHeight='200px'
              >
                <Typography color='error'>{error}</Typography>
              </Box>
            ) : sortedApps.length === 0 ? (
              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                minHeight='200px'
              >
                <Typography>No applications available</Typography>
              </Box>
            ) : (
              sortedApps.map((app) => (
                <React.Fragment key={app.applicationId}>
                  <div
                    className='applications__card'
                    onClick={() => handleLaunchApp(app)}
                  >
                    <img
                      src={app.logoData}
                      alt='App Icon'
                      className='applications__card-icon'
                    />
                    <div className='applications__card-content'>
                      <Typography className='applications__card-contentFont'>
                        {app.title}
                      </Typography>
                    </div>
                  </div>
                  {app.title === 'Proactis' && showEdgeInstallPrompt && (
                    <Box className='applications__edgeBanner' role='alert'>
                      <Typography className='applications__edgeBanner-text'>
                        Don&apos;t have Microsoft Edge installed? Install it
                        here
                      </Typography>
                      <Box
                        component='a'
                        href={EDGE_APP_STORE_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='applications__edgeBanner-link'
                      >
                        Install Edge
                      </Box>
                      <Button
                        size='small'
                        onClick={() => setShowEdgeInstallPrompt(false)}
                        className='applications__edgeBanner-dismiss'
                        aria-label='Dismiss'
                      >
                        &times;
                      </Button>
                    </Box>
                  )}
                </React.Fragment>
              ))
            )}
          </div>
          <Box className='applications__footer'>
            <Box className='applications__footer-gradientContainer'>
              <div className='applications__footer-gradient' />
            </Box>
            <div className='applications__footer-logos'>
              <Bet365LogoBlack className='applications__footer-logos-bet365' />
              <EmployeePortalLogoGreen className='applications__footer-logos-portal' />
            </div>
            <div className='applications__footer-logos-triangles' />
          </Box>
          <Box className='applications__logout'>
            <Button
              onClick={handleLogout}
              className='applications__logout-button'
            >
              <LockIcon /> <span>Logout</span>
            </Button>
            {isAdmin && (
              <Button
                onClick={() => navigate('/Administration')}
                className='applications__logout-button'
              >
                <span>Administration</span>
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default Applications
